import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { User } from './auth.service';

export interface UserListResponse {
    users: User[];
    total: number;
    page: number;
    per_page: number;
}

export interface ChangeRoleRequest {
    role: string;
}

export interface UpdateUserRequest {
    first_name?: string;
    last_name?: string;
    phone?: string;
    business_title?: string;
}

export interface ChangePasswordRequest {
    current_password: string;
    new_password: string;
}

export interface ResetPasswordResponse {
    email: string;
    temporary_password: string;
    message: string;
}

@Injectable({
    providedIn: 'root'
})
export class UserManagementService {
    private readonly API_URL = environment.apiUrl;

    constructor(private http: HttpClient) { }

    /** Admin only: autocomplete search (email / first / last name). */
    searchUsers(q: string, limit: number = 25): Observable<User[]> {
        const params = new HttpParams().set('q', q).set('limit', String(limit));
        return this.http.get<User[]>(`${this.API_URL}/users/search`, { params });
    }

    // Get all users (Admin only)
    getAllUsers(skip: number = 0, limit: number = 10): Observable<UserListResponse> {
        const params = new HttpParams()
            .set('skip', skip.toString())
            .set('limit', limit.toString());

        return this.http.get<UserListResponse>(`${this.API_URL}/users/`, { params });
    }

    // Get current user info
    getCurrentUserInfo(): Observable<User> {
        return this.http.get<User>(`${this.API_URL}/users/me`);
    }

    // Get user by ID (Admin only)
    getUserById(userId: string): Observable<User> {
        return this.http.get<User>(`${this.API_URL}/users/${userId}`);
    }

    // Change user role (Admin only)
    changeUserRole(userId: string, roleRequest: ChangeRoleRequest): Observable<User> {
        return this.http.patch<User>(`${this.API_URL}/users/${userId}/role`, roleRequest);
    }

    // Update current user
    updateCurrentUser(updateRequest: UpdateUserRequest): Observable<User> {
        return this.http.patch<User>(`${this.API_URL}/users/me`, updateRequest);
    }

    // Change password
    changePassword(passwordRequest: ChangePasswordRequest): Observable<any> {
        return this.http.post(`${this.API_URL}/users/me/change-password`, passwordRequest);
    }

    // Toggle user status (Admin only)
    toggleUserStatus(userId: string, isActive: boolean): Observable<any> {
        return this.http.patch(`${this.API_URL}/users/${userId}/status`, { is_active: isActive });
    }

    // Reset user password (Admin only) - generates temporary password
    resetUserPassword(userId: string): Observable<ResetPasswordResponse> {
        return this.http.post<ResetPasswordResponse>(`${this.API_URL}/users/${userId}/reset-password`, {});
    }
}
